import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

// Contexto para configuración de sponsors
const SponsorContext = createContext();

// Configuración por defecto
const DEFAULT_SPONSOR_CONFIG = {
  settings: {
    rotationEnabled: true,
    trackingEnabled: true,
    displayInSidebar: true,
    displayInLogin: true
  },
  priballConfigs: {
    lunes: { logoType: 'square', bgColor: 'light', featured: true },
    martes: { logoType: 'wide', bgColor: 'dark', featured: false },
    miercoles: { logoType: 'wide', bgColor: 'dark', featured: true },
    jueves: { logoType: 'wide', bgColor: 'dark', featured: false },
    viernes: { logoType: 'square', bgColor: 'light', featured: true },
    sabado: { logoType: 'wide', bgColor: 'dark', featured: false },
    domingo: { logoType: 'square', bgColor: 'light', featured: false }
  }
};

// Hook personalizado para usar el contexto
export const useSponsor = () => {
  const context = useContext(SponsorContext);
  if (!context) {
    throw new Error('useSponsor debe usarse dentro de SponsorProvider');
  }
  return context;
};

export const SponsorProvider = ({ children }) => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetch, setLastFetch] = useState(0);
  const [cacheExpiry] = useState(5 * 60 * 1000); // 5 minutos

  // Helper seguro para obtener hora de Argentina sin romper en navegadores que no soportan IANA TZ
  const getArgentinaTimeSafe = (date = new Date()) => {
    try {
      return new Date(date.toLocaleString('en-US', { timeZone: 'America/Argentina/Buenos_Aires' }));
    } catch (_) {
      const utcMs = date.getTime() + (date.getTimezoneOffset() * 60000);
      return new Date(utcMs - 3 * 60 * 60 * 1000); // UTC-3 como aproximación
    }
  };

  // ✅ FUNCIÓN OPTIMIZADA: Cargar configuración con cache
  const loadConfig = useCallback(async (forceRefresh = false) => {
    const now = Date.now();
    
    // Verificar cache
    if (!forceRefresh && config && (now - lastFetch) < cacheExpiry) {
      return config;
    }

    try {
      setLoading(true);
      setError(null);
      
      const getSponsorConfig = httpsCallable(functions, 'getSponsorConfig');
      const result = await getSponsorConfig();
      
      const loadedConfig = result.data.config;
      setConfig(loadedConfig);
      setLastFetch(now);
      
      return loadedConfig;
    } catch (err) {
      console.warn('⚠️ Error cargando configuración de sponsor, usando valores por defecto:', err);
      setError(err.message);
      setConfig(DEFAULT_SPONSOR_CONFIG);
      setLastFetch(now);
      return DEFAULT_SPONSOR_CONFIG;
    } finally {
      setLoading(false);
    }
  }, [config, lastFetch, cacheExpiry]);

  // ✅ FUNCIÓN OPTIMIZADA: Obtener configuración del día actual
  const getCurrentDayConfig = useCallback(() => {
    if (!config) return DEFAULT_SPONSOR_CONFIG.priballConfigs.lunes;

    // Si la rotación está deshabilitada, usar configuración de lunes
    if (!config.settings?.rotationEnabled) {
      return config.priballConfigs?.lunes || DEFAULT_SPONSOR_CONFIG.priballConfigs.lunes;
    }

    // Obtener día actual en Argentina (seguro)
    const argentinaTime = getArgentinaTimeSafe();
    
    const dayNames = ['domingo', 'lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado'];
    const todayName = dayNames[argentinaTime.getDay()];
    
    return config.priballConfigs?.[todayName] || DEFAULT_SPONSOR_CONFIG.priballConfigs[todayName];
  }, [config]);

  // ✅ FUNCIÓN OPTIMIZADA: Tracking simplificado
  const trackClick = useCallback(async (trackingData) => {
    if (!config?.settings?.trackingEnabled) return;

    try {
      const trackSponsorClick = httpsCallable(functions, 'trackSponsorClick');
      
      // ✅ DATOS MÍNIMOS NECESARIOS
      const minimalData = {
        userId: trackingData.userId,
        sponsorName: trackingData.sponsorName,
        routineId: trackingData.routineId,
        logoType: trackingData.logoType,
        bgColor: trackingData.bgColor,
        featured: trackingData.featured,
        timestamp: new Date().toISOString(),
        device: trackingData.device || 'unknown'
      };

      await trackSponsorClick(minimalData);
    } catch (error) {
      console.error('❌ Error en tracking:', error);
    }
  }, [config]);

  // ✅ FUNCIÓN: Verificar si se debe mostrar el sponsor
  const shouldDisplay = useCallback((location = 'sidebar') => {
    if (!config) return true;
    
    switch (location) {
      case 'sidebar':
        return config.settings?.displayInSidebar !== false;
      case 'login':
        return config.settings?.displayInLogin !== false;
      default:
        return true;
    }
  }, [config]);

  // Cargar configuración al montar
  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const value = {
    config,
    loading,
    error,
    getCurrentDayConfig,
    trackClick,
    shouldDisplay,
    refreshConfig: () => loadConfig(true)
  };

  return (
    <SponsorContext.Provider value={value}>
      {children}
    </SponsorContext.Provider>
  );
}; 